import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function GalleryList() {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchGalleries = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/gallery/list", {
        search: search,
        page: page,
        limit: rowsPerPage,
      });

      if (response?.success) {
        setGalleries(response.gallery || []);
        setTotalPages(response.totalPages || 0);
      } else {
        toast.error(response?.message || "Failed to fetch galleries");
      }
    } catch (error) {
      toast.error("Error fetching galleries");
      console.error("Fetch galleries error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleries(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchGalleries(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchGalleries(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/gallery/delete`, { id });
        if (response.success) {
          toast.success("Gallery item deleted successfully");
          fetchGalleries(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete gallery item");
        }
      } catch (error) {
        toast.error("Error deleting gallery item");
        console.error("Delete gallery error:", error);
      }
    }
  };

  const handleStatusChange = async (gallery) => {
    const newStatus = gallery.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this gallery item?`,
      text: `The gallery item will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/gallery/update-status`, {
          id: gallery._id,
          status: newStatus,
        });
        if (response.success) {
          toast.success(`Gallery item ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          fetchGalleries(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || `Failed to ${newStatus === "active" ? "activate" : "deactivate"} gallery item`);
        }
      } catch (error) {
        toast.error("Error updating gallery item status");
        console.error("Update gallery status error:", error);
      }
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      event: "Event",
      team: "Team",
      office: "Office",
      achievement: "Achievement",
      other: "Other",
    };
    return categories[category] || category;
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Gallery Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Gallery</li>
                  <li className="breadcrumb-item active">Gallery List</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/add-gallery")}
                disabled={loading}
              >
                <i className="fas fa-plus me-1"></i> Add Gallery
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Gallery Items</h5>
                </div>
                <div className="card-body">
                  {/* Search Form */}
                  <form onSubmit={handleSubmit} className="mb-4">
                    <div className="row g-3">
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by title..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <button type="submit" className="btn btn-primary w-100">
                          Search
                        </button>
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100"
                          onClick={handleReset}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {galleries.length > 0 ? (
                            galleries.map((gallery, index) => (
                              <tr key={gallery._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  {gallery.images && gallery.images.length > 0 ? (
                                    <div className="d-flex gap-1">
                                      {gallery.images.slice(0, 3).map((image, index) => (
                                        <img
                                          key={index}
                                          src={image}
                                          alt={`${gallery.title} ${index + 1}`}
                                          style={{ width: 40, height: 30, objectFit: "cover" }}
                                        />
                                      ))}
                                      {gallery.images.length > 3 && (
                                        <span className="badge bg-secondary">+{gallery.images.length - 3}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">No images</span>
                                  )}
                                </td>
                                <td>{gallery.title}</td>
                                <td>
                                  <span className="badge bg-info">
                                    {getCategoryLabel(gallery.category)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${gallery.status === "active" ? "success" : "danger"}`}
                                  >
                                    {gallery.status}
                                  </span>
                                </td>
                                <td>
                                  {new Date(gallery.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-gallery`, {
                                          state: { galleryData: gallery },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${gallery.status === "active" ? "warning" : "success"}`}
                                      onClick={() => handleStatusChange(gallery)}
                                      title={gallery.status === "active" ? "Deactivate" : "Activate"}
                                    >
                                      <i className={`mdi mdi-${gallery.status === "active" ? "close" : "check"}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(gallery._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            !loading && (
                              <tr>
                                <td colSpan="7" className="text-center py-4">
                                  No gallery items found
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GalleryList;
